import type { CentPrecisionMoney, Variant } from '@storefront/lib-global/codegen/types';
import { getAmount, getCurrencySymbol } from '@storefront/lib-global/utils/currencyHelper';
import { findMostExpensiveSku } from '@storefront/lib-global/utils/price';

type FindSelectedSkuProps = {
  selectedWidth: string;
  selectedSize: string;
  variants: Variant[];
};

function findSelectedSku({ selectedSize, selectedWidth, variants }: FindSelectedSkuProps) {
  return variants.find(
    (variant) => variant.size === selectedSize && (selectedWidth ? variant.fitLabel === selectedWidth : true),
  )?.price?.[0];
}

type CentPrecisionMoneyKeys =
  | 'wasPriceCentPrecisionMoney'
  | 'thenPriceCentPrecisionMoney'
  | 'actualPriceCentPrecisionMoney';

interface UseProductReturnType {
  wasPrice: string;
  thenPrice: string;
  actualPrice: string;
  wasPriceCentPrecisionMoney: Partial<CentPrecisionMoney>;
  thenPriceCentPrecisionMoney: Partial<CentPrecisionMoney>;
  actualPriceCentPrecisionMoney: Partial<CentPrecisionMoney>;
}

export function useProductPrice({ selectedSize, selectedWidth, variants }: FindSelectedSkuProps) {
  const selectedSku = findSelectedSku({ selectedSize, selectedWidth, variants });
  const mostExpensiveSku = findMostExpensiveSku({ variants });

  const {
    wasPrice,
    thenPrice,
    actualPrice,
    wasPriceCentPrecisionMoney,
    thenPriceCentPrecisionMoney,
    actualPriceCentPrecisionMoney,
  } = Object.entries(selectedSku ?? mostExpensiveSku ?? {})
    .filter(([_, priceObject]) => priceObject !== null && typeof priceObject === 'object')
    .reduce((acc, [priceType, priceObject]) => {
      const price = Array.isArray(priceObject) ? priceObject[priceObject.length - 1] : priceObject;
      const { centAmount, currencyCode } = price;
      acc[priceType as keyof UseProductReturnType] = `${getCurrencySymbol(currencyCode)}${getAmount(centAmount)}`;
      const key = `${priceType}CentPrecisionMoney` as CentPrecisionMoneyKeys;
      acc[key] = { centAmount } as Partial<CentPrecisionMoney>;

      return acc;
    }, {} as UseProductReturnType);

  const { toDate, fromDate, lastOfferedAtDate } = selectedSku ?? mostExpensiveSku ?? {};

  return {
    wasPrice,
    thenPrice,
    actualPrice,
    wasPriceCentPrecisionMoney,
    thenPriceCentPrecisionMoney,
    actualPriceCentPrecisionMoney,
    toDate,
    fromDate,
    lastOfferedAtDate,
  };
}
